/**
 * Hashes fields for GA4's user-provided data collection
 * (https://support.google.com/analytics/answer/14077171). Google's policy
 * requires PII to be SHA256-hashed before it reaches Google — normalization
 * must match Google's own (trim + lowercase) or the hash never matches their
 * records and the data collection silently no-ops.
 */

import { FieldType, FormDefinition } from "@/features/forms/types";

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function normalizeName(name: string): string {
  return name.trim().toLowerCase();
}

// Google expects phone numbers in E.164 shape before hashing; strip everything
// but digits and a leading "+" rather than attempting full E.164 conversion,
// which would need a country code we don't reliably have.
function normalizePhoneNumber(phone: string): string {
  return phone.trim().replace(/[^\d+]/g, "");
}

async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value)
  );
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

async function hashIfPresent(
  value: string | undefined,
  normalize: (value: string) => string
): Promise<string | undefined> {
  if (!value) return value;
  return sha256Hex(normalize(value));
}

/**
 * Hashes an email address for GA4's `sha256_email_address` user-data field.
 */
export async function hashEmail(
  email: string | undefined
): Promise<string | undefined> {
  return hashIfPresent(email, normalizeEmail);
}

/**
 * Hashes a phone number for GA4's `sha256_phone_number` user-data field.
 */
export async function hashPhoneNumber(
  phone: string | undefined
): Promise<string | undefined> {
  return hashIfPresent(phone, normalizePhoneNumber);
}

/**
 * Hashes a first name for GA4's `address.sha256_first_name` user-data field.
 */
export async function hashFirstName(
  firstName: string | undefined
): Promise<string | undefined> {
  return hashIfPresent(firstName, normalizeName);
}

/**
 * Hashes a last name for GA4's `address.sha256_last_name` user-data field.
 */
export async function hashLastName(
  lastName: string | undefined
): Promise<string | undefined> {
  return hashIfPresent(lastName, normalizeName);
}

function getFormDataByName(
  formData: Record<string, unknown>,
  formDefinition: FormDefinition,
  name: string,
  type: FieldType = "text"
): string | undefined {
  const fieldName = formDefinition.fields.find(
    (field) => field.name.toLocaleLowerCase() === name && field.type === type
  )?.name;
  if (!fieldName) return undefined;
  return String(formData[fieldName]);
}

function getFormDataByType(
  formData: Record<string, unknown>,
  formDefinition: FormDefinition,
  type: FieldType = "text"
): string | undefined {
  const fieldName = formDefinition.fields.find(
    (field) => field.type === type
  )?.name;
  if (!fieldName) return undefined;
  return String(formData[fieldName]);
}

interface GoogleUserData {
  sha256_email_address?: string;
  sha256_phone_number?: string;
  address?: {
    sha256_first_name?: string;
    sha256_last_name?: string;
  };
}

export async function getFormUserData(
  formData: Record<string, unknown>,
  formDefinition: FormDefinition
): Promise<GoogleUserData> {
  const [email, phone, firstName, lastName] = await Promise.all([
    hashEmail(getFormDataByType(formData, formDefinition, "email")),
    hashPhoneNumber(getFormDataByType(formData, formDefinition, "phone")),
    hashFirstName(getFormDataByName(formData, formDefinition, "firstname")),
    hashLastName(getFormDataByName(formData, formDefinition, "lastname")),
  ]);

  return {
    sha256_email_address: email,
    sha256_phone_number: phone,
    address: {
      sha256_first_name: firstName,
      sha256_last_name: lastName,
    },
  };
}
